import { useState } from "react";
import { Link } from "react-router-dom";
import { MailCheck } from "lucide-react";
import { AuthLayout, inputClass } from "@/components/AuthLayout";
import { apiFetch } from "@/api";

const EMAIL_RE = /^\S+@\S+\.\S+$/;

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value || !EMAIL_RE.test(value)) {
      setError("Enter a valid email address");
      return;
    }
    setError("");
    setSending(true);
    try {
      await apiFetch("/auth/forgot-password", {
        method: "POST",
        body: { email: value },
      });
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We'll email you a secure reset link."
      footer={
        <>
          Remembered it?{" "}
          <Link
            to="/login"
            data-testid="back-to-login-link"
            className="font-bold text-[#16A34A] hover:underline"
          >
            Back to login
          </Link>
        </>
      }
    >
      {sent ? (
        <div data-testid="forgot-sent" className="text-center">
          <div className="mx-auto mb-4 inline-flex rounded-2xl bg-[#16A34A]/10 p-3.5 text-[#16A34A]">
            <MailCheck className="h-7 w-7" />
          </div>
          <p className="text-sm font-semibold leading-relaxed text-[#17201A]/80">
            If an account exists for{" "}
            <span className="font-bold text-[#17201A]">{email.trim()}</span>, a
            reset link is on its way. It stays valid for 1 hour.
          </p>
          <p className="mt-3 text-xs text-[#647067]">
            Don't see it? Check your spam folder.
          </p>
        </div>
      ) : (
        <form
          data-testid="forgot-password-form"
          onSubmit={handleSubmit}
          className="space-y-4"
          noValidate
        >
          <div>
            <label className="mb-1.5 block text-sm font-bold text-[#17201A]">
              Registered Email
            </label>
            <input
              data-testid="forgot-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className={inputClass}
            />
            {error && (
              <p
                data-testid="forgot-email-error"
                className="mt-1.5 text-xs font-semibold text-red-500"
              >
                {error}
              </p>
            )}
          </div>
          <button
            type="submit"
            data-testid="forgot-submit-btn"
            data-magnetic="true"
            disabled={sending}
            className="w-full rounded-full bg-[#16A34A] py-4 text-base font-bold text-white shadow-sm transition-[background-color,transform] duration-200 hover:bg-[#15803D] disabled:opacity-60"
          >
            {sending ? "Sending..." : "Send Reset Link"}
          </button>
        </form>
      )}
    </AuthLayout>
  );
};

export default ForgotPassword;
